//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local Libs
import Service from './service';
import CustomerLeadModel from '../models/customer-lead.model';

/**
 * @service CustomerLeadService
 * @description Service for managing customer lead data operations.
 * Provides HTTP methods for retrieving customer leads from the backend API.
 * Customer leads represent potential business opportunities or inquiries within the trading community.
 * Extends base Service class with generic type CustomerLeadModel.
 *
 * @feature 1. Fetch all customer leads with pagination (large page size)
 * @feature 2. Cancel ongoing HTTP requests to prevent memory leaks
 * @feature 3. Promise-based async operations for lead management
 *
 * @architecture
 * Extends base Service<CustomerLeadModel> providing REST API integration with configurable base URL
 * and service endpoint. Implements request tracking for cleanup and cancellation. Uses HttpClient
 * for HTTP operations with standard error handling.
 *
 * @service-endpoint /tradingcommunity/customerleads/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerLeadService extends Service<CustomerLeadModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/customerleads/');
  }

  /**
   * @method getAllCustomerLeads
   * @async
   * @description Fetch all customer lead records from the backend
   *
   * Makes HTTP GET request to backend endpoint /tradingcommunity/customerleads/
   * with query parameters for page size (50000) and order (new). Wraps response in Promise for
   * async/await compatibility.
   *
   * @returns {Promise<Array<CustomerLeadModel>>} Promise resolving to array of CustomerLeadModel objects
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests
   * - Subscription can be cancelled via cancelOnGoingHttpRequests()
   *
   * @throws Rejects promise with HttpErrorResponse if request fails
   * @example
   * const leads = await customerLeadService.getAllCustomerLeads();
   * console.log(leads); // Array of CustomerLeadModel objects
   */
  public getAllCustomerLeads() {
    return new Promise<Array<CustomerLeadModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CustomerLeadModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=50000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }
  
  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.customerLeadService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
